import React, { useState, useMemo, useEffect } from 'react';
import { SettingsIcon, DollarSignIcon, BarChartIcon, UsersIcon, AwardIcon, SlidersHorizontalIcon, PlusCircleIcon, XCircleIcon, LightbulbIcon } from '../lib/icons';
import { Card, CardHeader, Input, Select, Button, LineChart } from './ui';
import { AUCTION_TYPES, COMPETITOR_TYPES, PROJECT_TYPES, COST_STRUCTURES, CURRENCIES } from '../lib/constants';
import { runAuctioneerSimulation } from '../lib/auctioneerSimulation';

export default function AuctioneerDashboard() {
    const [activeTooltip, setActiveTooltip] = useState(null);

    // State for Auctioneer Dashboard
    const [auctionType, setAuctionType] = useState('FIRST_PRICE');
    const [currency, setCurrency] = useState('USD');
    const [projectType, setProjectType] = useState('PV_SOLAR');
    const [maxBudget, setMaxBudget] = useState(200000000);
    const [targetBudget, setTargetBudget] = useState(150000000);
    
    const [qualityWeights, setQualityWeights] = useState({
        priceWeight: 80,
        techWeight: 12,
        expWeight: 8,
    });

    const [bidderProfiles, setBidderProfiles] = useState([
        { type: 'AGGRESSIVE', count: 2, costMean: 155_000_000, costStdDev: 5_000_000, techScore: 85, expScore: 88 },
        { type: 'ESTABLISHED', count: 2, costMean: 160_000_000, costStdDev: 8_000_000, techScore: 92, expScore: 95 },
    ]);

    const [reservePrice, setReservePrice] = useState(180000000);
    const [simulationResults, setSimulationResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const numBidders = useMemo(() => bidderProfiles.reduce((sum, profile) => sum + profile.count, 0), [bidderProfiles]);
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: CURRENCIES[currency].name,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const handleBidderChange = (index, field, value) => {
        const newBidders = [...bidderProfiles];
        if (field === 'type') {
            newBidders[index][field] = value;
        } else {
            newBidders[index][field] = Number(value);
        }
        setBidderProfiles(newBidders);
    };
    
    const addBidderProfile = () => {
        setBidderProfiles([...bidderProfiles, { 
            type: 'AGGRESSIVE', 
            count: 1, 
            costMean: 150_000_000, 
            costStdDev: 5_000_000,
            techScore: 85,
            expScore: 88
        }]);
    };
    
    const removeBidderProfile = (index) => {
        setBidderProfiles(bidderProfiles.filter((_, i) => i !== index));
    };

    const handleRunSimulation = () => {
        setIsLoading(true);
        setSimulationResults(null);

        if (numBidders < 2) {
            alert('You need at least 2 bidders to run an auction simulation.');
            setIsLoading(false);
            return;
        }

        if (numBidders > 100) {
            alert('Too many bidders. Please reduce to 100 or fewer for optimal performance.');
            setIsLoading(false);
            return;
        }

        const numSims = numBidders > 50 ? 1000 : 
                       numBidders > 30 ? 2000 : 
                       numBidders > 20 ? 3000 : 
                       numBidders > 10 ? 4000 : 5000;

        const runSimulationAsync = async () => {
            try {
                const results = [];
                
                // For auctioneer perspective, we want to see expected winning bid across different scenarios
                for (let i = 0; i <= 20; i++) {
                    if (i > 0 && i % 5 === 0) {
                        await new Promise(resolve => setTimeout(resolve, 1));
                    }
                    
                    const simResult = runAuctioneerSimulation({
                        bidderProfiles,
                        qualityWeights,
                        reservePrice,
                        numSimulations: numSims,
                        auctionType,
                        iteration: i
                    });
                    
                    results.push({
                        scenario: i,
                        expectedWinningBid: simResult.expectedWinningBid,
                        winningBidStdDev: simResult.winningBidStdDev,
                        averageWinnerTechScore: simResult.averageWinnerTechScore,
                        averageWinnerExpScore: simResult.averageWinnerExpScore,
                        budgetSavings: targetBudget - simResult.expectedWinningBid,
                        savingsPercentage: ((targetBudget - simResult.expectedWinningBid) / targetBudget) * 100
                    });
                }
                setSimulationResults(results);
            } catch (error) {
                console.error('Simulation error:', error);
                setSimulationResults([]);
            }
            setIsLoading(false);
        };

        runSimulationAsync();
    };

    useEffect(() => {
        setSimulationResults(null);
    }, [auctionType]);

    const summaryData = useMemo(() => {
        if (!simulationResults || simulationResults.length === 0) return null;
        
        const avgResult = simulationResults[Math.floor(simulationResults.length / 2)];
        return {
            expectedWinningBid: avgResult.expectedWinningBid,
            budgetSavings: avgResult.budgetSavings,
            savingsPercentage: avgResult.savingsPercentage,
            techScore: avgResult.averageWinnerTechScore,
            expScore: avgResult.averageWinnerExpScore
        };
    }, [simulationResults]);

    return (
      <div onClick={() => setActiveTooltip(null)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* --- INPUTS COLUMN --- */}
            <div className="lg:col-span-1 flex flex-col space-y-6">
                <Card>
                    <CardHeader icon={<SettingsIcon />} title="Auction Setup" />
                    <div className="p-4 space-y-4">
                        <Select 
                            id="auction-type"
                            label="Auction Format" 
                            value={auctionType} 
                            onChange={e => setAuctionType(e.target.value)} 
                            options={AUCTION_TYPES} 
                            tooltip="Choose the auction format. Each has different strategic implications for bidders and expected outcomes for you as the buyer."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                         <Select 
                            id="currency-type"
                            label="Currency" 
                            value={currency} 
                            onChange={e => setCurrency(e.target.value)} 
                            options={CURRENCIES} 
                            tooltip="Select the currency for all monetary values in the auction."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                            displaySymbol={true}
                        />
                    </div>
                </Card>
                <Card>
                    <CardHeader icon={<DollarSignIcon />} title="Project & Budget" />
                    <div className="p-4 space-y-3">
                        <Select 
                            id="project-type"
                            label="Project Type"
                            value={projectType}
                            onChange={(e) => setProjectType(e.target.value)}
                            options={PROJECT_TYPES}
                            tooltip="Select the type of renewable energy project you want to procure."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <Input 
                            id="max-budget"
                            label="Maximum Budget" 
                            value={maxBudget} 
                            onChange={e => setMaxBudget(Number(e.target.value))} 
                            unit={CURRENCIES[currency].symbol}
                            tooltip="The absolute maximum you can spend on this project. This sets your reserve price ceiling."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <Input 
                            id="target-budget"
                            label="Target Budget" 
                            value={targetBudget} 
                            onChange={e => setTargetBudget(Number(e.target.value))} 
                            unit={CURRENCIES[currency].symbol}
                            tooltip="Your ideal budget for this project. The simulation will show savings relative to this target."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <Input 
                            id="reserve-price"
                            label="Reserve Price" 
                            value={reservePrice} 
                            onChange={e => setReservePrice(Number(e.target.value))} 
                            unit={CURRENCIES[currency].symbol}
                            tooltip="The maximum price you're willing to pay. If all bids exceed this, you don't award the contract."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                    </div>
                </Card>
            </div>

            {/* --- MIDDLE COLUMN --- */}
            <div className="lg:col-span-2 flex flex-col space-y-6">
                <Card>
                    <CardHeader icon={<UsersIcon />} title="Expected Bidder Profiles" subtitle={`Modeling ${numBidders} potential bidders.`} />
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {bidderProfiles.map((bidder, index) => (
                            <div key={index} className="p-3 bg-gray-900/50 rounded-lg border border-gray-700 relative">
                                <button onClick={() => removeBidderProfile(index)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><XCircleIcon /></button>
                                <Select 
                                    id={`bidder-type-${index}`}
                                    label="Bidder Type" 
                                    value={bidder.type} 
                                    onChange={e => handleBidderChange(index, 'type', e.target.value)} 
                                    options={COMPETITOR_TYPES}
                                    tooltip="The competitive profile of this bidder group. This affects their cost structure and bidding aggressiveness."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`bidder-count-${index}`}
                                    label="Count" 
                                    value={bidder.count} 
                                    onChange={e => handleBidderChange(index, 'count', e.target.value)}
                                    tooltip="Number of bidders in this category. More bidders generally lead to lower winning bids."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`bidder-cost-mean-${index}`}
                                    label="Avg. True Cost" 
                                    value={bidder.costMean} 
                                    onChange={e => handleBidderChange(index, 'costMean', e.target.value)} 
                                    unit={CURRENCIES[currency].symbol}
                                    tooltip="Your estimate of this bidder type's average project cost. This is their baseline before markup."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`bidder-cost-stddev-${index}`}
                                    label="Cost Uncertainty" 
                                    value={bidder.costStdDev} 
                                    onChange={e => handleBidderChange(index, 'costStdDev', e.target.value)} 
                                    unit={CURRENCIES[currency].symbol}
                                    tooltip="How much this bidder type's costs vary. Higher uncertainty affects their bidding strategy."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`bidder-tech-${index}`}
                                    label="Tech Score" 
                                    value={bidder.techScore} 
                                    onChange={e => handleBidderChange(index, 'techScore', e.target.value)} 
                                    unit="/ 100"
                                    tooltip="Expected technical quality score for this bidder type."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`bidder-exp-${index}`}
                                    label="Experience Score" 
                                    value={bidder.expScore} 
                                    onChange={e => handleBidderChange(index, 'expScore', e.target.value)} 
                                    unit="/ 100"
                                    tooltip="Expected experience/reputation score for this bidder type."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                            </div>
                        ))}
                         <button onClick={addBidderProfile} className="w-full text-blue-400 hover:text-blue-300 flex items-center justify-center p-2 border-2 border-dashed border-gray-600 rounded-lg hover:border-blue-500 transition md:col-span-2">
                            <PlusCircleIcon className="mr-2"/> Add Bidder Group
                        </button>
                    </div>
                </Card>
                <Card>
                    <CardHeader icon={<AwardIcon />} title="Evaluation Criteria" />
                    <div className="p-4 space-y-3">
                        <Input 
                            id="price-weight"
                            label="Price Weight (%)" 
                            value={qualityWeights.priceWeight} 
                            onChange={e => setQualityWeights(p => ({...p, priceWeight: Number(e.target.value)}))} 
                            unit="%"
                            tooltip="How much weight price has in your final decision (0-100%). Higher weight favors lowest bidders."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <Input 
                            id="tech-weight"
                            label="Technical Weight (%)" 
                            value={qualityWeights.techWeight} 
                            onChange={e => setQualityWeights(p => ({...p, techWeight: Number(e.target.value)}))} 
                            unit="%"
                            tooltip="Weight for technical quality in evaluation. All weights should sum to 100%."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <Input 
                            id="exp-weight"
                            label="Experience Weight (%)" 
                            value={qualityWeights.expWeight} 
                            onChange={e => setQualityWeights(p => ({...p, expWeight: Number(e.target.value)}))} 
                            unit="%"
                            tooltip="Weight for experience/reputation in evaluation. All weights should sum to 100%."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <div className="pt-2 border-t border-gray-700">
                           <p className="text-sm text-gray-400">Total Weight:</p>
                           <p className={`text-xl font-bold ${(qualityWeights.priceWeight + qualityWeights.techWeight + qualityWeights.expWeight) === 100 ? 'text-green-400' : 'text-red-400'}`}>
                               {qualityWeights.priceWeight + qualityWeights.techWeight + qualityWeights.expWeight}%
                           </p>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
        <div className="mt-6">
             <Card>
                <CardHeader icon={<BarChartIcon />} title="Auction Outcome Analysis" />
                <div className="p-4">
                    <div className="mb-6">
                        <Button onClick={handleRunSimulation} disabled={isLoading}>
                            {isLoading ? 'Running Simulation...' : 'Analyze Expected Outcomes'}
                        </Button>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">Simulate thousands of auction scenarios to predict winning bids, quality scores, and budget savings. This helps you optimize auction design and set realistic expectations.</p>
                     
                    {simulationResults && (
                        <div className="mt-6">
                            <h4 className="text-lg font-semibold text-white mb-4">Expected Auction Results</h4>
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-500/30">
                                    <p className="text-sm text-blue-300">Expected Winning Bid</p>
                                    <p className="text-2xl font-bold text-white">{formatCurrency(summaryData.expectedWinningBid)}</p>
                                </div>
                                <div className="bg-green-900/30 p-4 rounded-lg border border-green-500/30">
                                    <p className="text-sm text-green-300">Budget Savings</p>
                                    <p className="text-2xl font-bold text-white">{formatCurrency(summaryData.budgetSavings)}</p>
                                    <p className="text-sm text-green-400">({summaryData.savingsPercentage.toFixed(1)}% below target)</p>
                                </div>
                                <div className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/30">
                                    <p className="text-sm text-purple-300">Winner Quality</p>
                                    <p className="text-lg font-bold text-white">Tech: {summaryData.techScore.toFixed(0)}/100</p>
                                    <p className="text-lg font-bold text-white">Exp: {summaryData.expScore.toFixed(0)}/100</p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-300">
                                    <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3">Auction Format</th>
                                            <th scope="col" className="px-6 py-3">Strategic Advantage</th>
                                            <th scope="col" className="px-6 py-3">Expected Price</th>
                                            <th scope="col" className="px-6 py-3">Quality Outcome</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">
                                                {AUCTION_TYPES[auctionType].name}
                                            </th>
                                            <td className="px-6 py-4">
                                                {auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH' ? 
                                                    'Revenue equivalent, strategic bidding' : 
                                                    'Truthful bidding, may yield lower prices'
                                                }
                                            </td>
                                            <td className="px-6 py-4 text-blue-400">{formatCurrency(summaryData.expectedWinningBid)}</td>
                                            <td className="px-6 py-4 text-purple-400">Tech: {summaryData.techScore.toFixed(0)} | Exp: {summaryData.expScore.toFixed(0)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
      </div>
    );
}